<template>
  <el-card v-if="model.conversions" v-loading="loading">
    <div class="goals-ct">
      <div class="header">
        <span class="header-title">
          {{ $t('CampaignAnalytics_Performance_Campaign_Goal_Title') }}
        </span>
        <date-filter @change="handlerDateFilter($event)" />
      </div>

      <div class="metric-ct">
        <div class="metric" v-for="goal in goalMetrics" :key="goal.name">
          <el-tooltip
            v-if="goal.tooltip"
            :content="
              $t('CampaignAnalytics_Performance_Conversion_Rate_Tool_Tip')
            "
            placement="top"
          >
            <div>
              <key-metric
                :title="
                  metricLabels.hasOwnProperty(goal.name)
                    ? $t(metricLabels[goal.name])
                    : goal.name
                "
                :metric="
                  $t('CampaignAnalytics_Performance_Conversion_Rate_N_A')
                "
                :type="goal.type"
                class="key-metric"
              />
            </div>
          </el-tooltip>

          <key-metric
            v-else
            :title="
              metricLabels.hasOwnProperty(goal.name)
                ? $t(metricLabels[goal.name])
                : goal.name
            "
            :metric="goal.value"
            :type="goal.type"
            class="key-metric"
          />
        </div>
      </div>
    </div>
  </el-card>
  <el-card v-else>
    <div class="flex-container">
      <icon
        class="flex-item"
        glyph="goal-conversion"
        size="50"
        style="color: #1d87c8"
      />
      <h2 class="flex-item">
        {{ $t('CampaignAnalytics_Performance_Goal_Conversion') }}
      </h2>
      <p class="flex-item">
        {{
          $t('CampaignAnalytics_Performance_Goal_Conversion_Description_First')
        }}
      </p>
      <p class="flex-item">
        {{
          $t('CampaignAnalytics_Performance_Goal_Conversion_Description_Second')
        }}
      </p>
      <div class="button-ct flex-item">
        <el-button
          @click="goToGoal"
          v-if="isLiveCampaign"
          type="primary"
          icon="el-icon-circle-plus-outline"
        >
          {{ $t('CampaignAnalytics_Performance_Set_Goal_Btn') }}
        </el-button>
        <a :href="docUrl" target="_blank">
          <el-button icon="el-icon-top-right">
            {{ $t('CampaignAnalytics_Performance_View_Docs_Btn') }}
          </el-button>
        </a>
      </div>
    </div>
  </el-card>
</template>

<script>
/* eslint-disable indent */
import { getDateFilterRange, projectEndpoint } from '@/core/helpers'
import moment from 'moment'
import RestApi from '@/core/services/RestApi'
import KeyMetric from '@/common/components/KeyMetric'
import { metricLabels } from '@/common/components/dashboard/Labels'
import DateFilter from '@/common/components/dashboard/DateFilter.vue'
import { $hasFeature } from '../../../../core/utils/VueAcl'

export default {
  name: 'PerformanceContent',
  components: { DateFilter, KeyMetric },
  props: {
    model: {
      type: Object,
      required: true
    }
  },

  data() {
    return {
      docUrl: 'https://docs.xtremepush.com/docs/campaign-setup#goal-conversion',
      data: [],
      dateFrom: '',
      dateTo: '',
      loading: false,
      metricLabels
    }
  },

  created() {
    let dateRange = getDateFilterRange()
    this.handlerDateFilter(dateRange)
  },

  methods: {
    goToGoal() {
      window.localStorage.setItem('goToGoal', true)
      this.$router.push({
        path: this.$route.path,
        query: {
          id: this.$route.query.id,
          project_id: this.$route.query.project_id
        },
        hash: '#setup'
      })
    },

    handlerDateFilter(dateRange) {
      this.dateFrom = moment(dateRange.start).format('YYYY-MM-DD')
      this.dateTo = moment(dateRange.end).format('YYYY-MM-DD')
      this.fetchData()
    },

    fetchData() {
      this.loading = true
      RestApi(
        projectEndpoint({
          method: 'get',
          url: `campaigns/${this.$route.query.id}/analytics`,
          params: {
            date_from: this.dateFrom,
            date_to: this.dateTo
          }
        })
      )
        .then(({ data }) => {
          this.data = data
        })
        .finally(() => {
          this.loading = false
        })
    }
  },

  computed: {
    isLiveCampaign() {
      return (
        this.model.hasOwnProperty('status_canonical') &&
        this.model.status_canonical === 'live'
      )
    },

    goalMetrics() {
      const goalMetrics = $hasFeature('enableNewGoalConversion')
        ? [
            'messages',
            'profile_triggers',
            'distinct_profiles',
            'profile_conversions',
            'distinct_profile_conversions',
            'profile_conversion_rate',
            'goal_value'
          ]
        : [
            'actions_sent_unique',
            'actions_converted_unique',
            'actions_conversion',
            'goals',
            'goals_value'
          ]

      if (
        !Object.hasOwn(this.data, 'goal') ||
        !this.data.goal ||
        this.data.goal.length === 0
      ) {
        return [
          {
            name: 'messages',
            value: 0
          },
          {
            name: 'profile_triggers',
            value: 0
          },
          {
            name: 'distinct_profiles',
            value: 0
          },
          {
            name: 'profile_conversions',
            value: 0
          },
          {
            name: 'distinct_profile_conversions',
            value: 0
          },
          {
            name: 'profile_conversion_rate',
            type: 'percentage',
            value: 0
          },
          {
            name: 'goal_value',
            value: 0
          }
        ]
      }
      return this.data.goal
        .filter(metric => goalMetrics.includes(metric.name))
        .map(metric => {
          if (metric.name === 'profile_conversion_rate') {
            const triggersMetric = this.data.goal.find(
              m => m.name === 'profile_triggers'
            )
            const triggersValue = triggersMetric ? triggersMetric.value : null

            if (triggersValue === 0) {
              return {
                ...metric,
                tooltip: true
              }
            }
          }
          return metric
        })
    }
  }
}
</script>

<style scoped>
.flex-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 20px;
  padding: 20px 0;
}

.flex-item {
  text-align: center;
}

p.flex-item {
  width: 350px;
  font-size: 16px;
  color: #7e8596;
}

h2 {
  font-size: 20px;
}

.metric-ct {
  display: inline-flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.goals-ct {
  display: flex;
  flex-direction: column;
  row-gap: 12px;
}

.key-metric {
  width: 260px;
  height: 100px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: start;
}

.header-title {
  align-self: center;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/analytics/PerformanceContent.vue